import { masterPermissionsSet } from './constants';
import { cookie } from './cookie';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Initialize dayjs plugins
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Formats date to Indian Standard Time with full details
 * @param {string | Date} date - Date to format
 * @returns {string} Formatted date like "January 12, 2024, 06:30 PM IST"
 */
export const formatDateToIST = (date) => {
  if (!date) return '-';

  try {
    return new Intl.DateTimeFormat('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
      timeZoneName: 'short'
    }).format(new Date(date));
  } catch (error) {
    console.error('Error formatting date to IST:', error);
    return '-';
  }
};

/**
 * Formats date to DD/MM/YYYY format
 * @param {string} dateString - Date string in any format
 * @returns {string} Formatted date like "12/01/2024"
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';

  try {
    let parsedDate = dayjs(dateString);

    if (!parsedDate.isValid()) {
      parsedDate = dayjs(dateString, 'DD/MM/YYYY', true);
    }

    return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : dateString;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Converts date to ISO format with timezone
 * @param {string | Date} date - Date to convert
 * @param {boolean} startOfDay - Whether to set time to start of day
 * @returns {string} ISO formatted date
 */
export const convertToISO = (date, startOfDay = true) => {
  if (!date) return null;

  try {
    let parsedDate;

    if (typeof date === 'string' && date.includes('/')) {
      parsedDate = dayjs(date, 'DD/MM/YYYY hh:mm A');
    } else {
      parsedDate = dayjs(date);
    }

    if (!parsedDate.isValid()) {
      console.error('Invalid date provided');
      return null;
    }

    parsedDate = parsedDate.tz('Asia/Kolkata');

    if (startOfDay) {
      parsedDate = parsedDate.startOf('day');
    }

    return parsedDate.toISOString();
  } catch (error) {
    console.error('Error converting to ISO:', error);
    return null;
  }
};

export const getFormattedDateTime = (date) => {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm:ss') : null;
};

export const normalizePhoneNumber = (phone) => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 10) {
    return digits.slice(-10);
  }
  return digits;
};

export const parseDate = (dateString) => {
  if (!dateString) return null;

  const [datePart, timePart, period] = dateString.split(' ');
  const [day, month, year] = datePart.split('/');
  const [hours, minutes] = timePart.split(':');

  let hour = parseInt(hours);
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  } else if (period === 'AM' && hour === 12) {
    hour = 0;
  }

  const date = new Date(year, month - 1, day, hour, parseInt(minutes));
  return date;
};

export const groupByDocumentCategoryEnum = (documents) => {
  return documents
    ? documents.reduce((acc, document) => {
        const category = document.documentCategoryEnum;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(document);
        return acc;
      }, {})
    : [];
};

export const debounce = (
  func,
  delay,
  options = { leading: false, trailing: true }
) => {
  let timer;
  let lastArgs;
  let lastInvokeTime = 0;

  const { leading, trailing } = options;

  const invokeFunc = (time) => {
    const args = lastArgs;
    lastArgs = null;
    lastInvokeTime = time;
    func(...args);
  };

  const debounced = (...args) => {
    const now = Date.now();

    if (!lastInvokeTime && leading) {
      invokeFunc(now);
    }

    clearTimeout(timer);
    lastArgs = args;

    timer = setTimeout(() => {
      if (trailing) {
        invokeFunc(now);
      }
    }, delay);
  };

  debounced.cancel = () => {
    clearTimeout(timer);
    lastArgs = null;
    lastInvokeTime = 0;
  };

  debounced.flush = () => {
    if (lastArgs) {
      invokeFunc(Date.now());
    }
  };

  return debounced;
};

export const convertToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const openWhatsApp = (phoneNumber, message) => {
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  window.open(whatsappUrl, '_blank');
};

export const openWhatsAppGroup = (groupLink, defaultMessage) => {
  const encodedMessage = encodeURIComponent(defaultMessage);
  const whatsappUrl = `${groupLink}?text=${encodedMessage}`;
  window.open(whatsappUrl, '_blank');
};

export const isEmpty = (obj) => {
  if (obj === null || obj === undefined) {
    return true;
  }
  if (typeof obj !== 'object') {
    return true;
  }
  return Object.keys(obj).length === 0;
};

export const generatePermissionSet = (rolePermissions = {}) => {
  const permissionSet = masterPermissionsSet.reduce((acc, permission) => {
    acc[permission] = rolePermissions[permission] || false;
    return acc;
  }, {});

  return permissionSet;
};

export const stringToColor = (string = '') => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

export const stringAvatar = (name = '') => {
  return {
    sx: {
      width: 56,
      height: 56,
      mr: 2,
      bgcolor: stringToColor(name)
    },
    children: `${name.split(' ')[0]}${name.split(' ')[1]}`
  };
};

export const getObjectSize = (obj = {}) => {
  let size = 0,
    key;

  for (key in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) size++;
  }
  return size;
};

export const isEqual = (value1, value2) => {
  // Check if both values are strictly equal
  if (value1 === value2) {
    return true;
  }

  // Handle null and undefined
  if (value1 == null || value2 == null) {
    return value1 === value2;
  }

  // Check if both values are of the same type
  if (typeof value1 !== typeof value2) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) {
      return false;
    }
    return value1.every((item, index) => isEqual(item, value2[index]));
  }

  // Handle objects
  if (typeof value1 === 'object' && typeof value2 === 'object') {
    const keys1 = Object.keys(value1);
    const keys2 = Object.keys(value2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    return keys1.every(
      (key) =>
        // eslint-disable-next-line no-prototype-builtins
        value2.hasOwnProperty(key) && isEqual(value1[key], value2[key])
    );
  }

  // Fallback for other types (e.g., functions, primitives)
  return value1 === value2;
};

export const areSetsEqual = (setA, setB) =>
  setA.size === setB.size && [...setA].every((value) => setB.has(value));

export const formatAmountToRupee = (amountInPaisa) => {
  const amountInRupees = amountInPaisa / 100;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amountInRupees);
};

export const formatAmountToPaise = (amountInRupees) => {
  const amountInPaisa = amountInRupees ? amountInRupees * 100 : 0;
  return amountInPaisa;
};

export const transformToLabelValue = (data) => {
  if (typeof data !== 'object' || data === null) {
    throw new Error('Invalid input: data should be a non-null object.');
  }

  const result = {};

  for (const [key, value] of Object.entries(data)) {
    const label = key
      .replace(/([A-Z])/g, ' $1') // to a space before capital letters
      .replace(/^./, (str) => str.toUpperCase());

    result[key] = { label, value };
  }
  return result;
};

export const transformNestedObjectToLabelValue = (data) => {
  if (typeof data !== 'object' || data === null) {
    throw new Error('Invalid input: data should be a non-null object.');
  }

  const transform = (obj) => {
    const result = {};

    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === '') {
        continue;
      }

      const label = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());

      if (typeof value === 'object' && !Array.isArray(value)) {
        const transformedValue = transform(value);
        if (Object.keys(transformedValue).length > 0) {
          result[key] = transformedValue;
        }
      } else {
        result[key] = { label, value };
      }
    }

    return result;
  };

  return transform(data);
};

export const getCapitalizedString = (str) => {
  if (typeof str === 'string') {
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  }
  throw new Error('Invalid string: data should be a non-null string.');
};

export const clearAllStorage = () => {
  // Clear localStorage
  window.localStorage.clear();
  // Clear sessionStorage
  window.sessionStorage.clear();
  // Remove all instances of token cookie (from all domains)
  cookie.eraseAll('token');
  cookie.eraseAll('user');
  window.location.reload();
};

export const documentTypeEnum = {
  invoiceDocumentCode: 'INVOICE',
  feasibilityApprovalDocCode: 'FEASIBILITY_APPROVAL',
  electricityBillImage: 'ELECTRICITY_BILL',
  rooftopSolarContract: 'ROOF_TOP_CONTRACT',
  signedRooftopSolarContract: 'SIGNED_ROOF_TOP_CONTRACT',
  detailedProjectReport: 'DPR',
  loanAgreement: 'LOAN_AGREEMENT',
  sanctionLetter: 'SANCTION_LETTER',
  signedLoanAgreement: 'SIGNED_LOAN_AGREEMENT',
  signedSanctionLetter: 'SIGNED_SANCTION_LETTER'
};

export const documentName = {
  loanAgreement: 'Loan Agreement',
  sanctionLetter: 'Sanction Letter',
  signedLoanAgreement: 'Signed Loan Agreement',
  signedSanctionLetter: 'Signed Sanction Letter'
};

export const userRoleByUserTypeCode = {
  INTERNAL_USER: 'admin',
  CUSTOMER: 'customer',
  ADMIN: 'admin',
  EPC: 'epc',
  DSA: 'dsa',
  MANUFACTURER_PARTNER: 'manufacturer'
};

export const truncateText = (text, maxLength = 30) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date);
};

export const toLakhs = (amount) => {
  const inLakhs = amount / 100000;

  return inLakhs.toFixed(2);
};

export const formatAmount = (amount) => {
  return '₹' + amount.toLocaleString('en-IN');
};

export const processEmail = (email) => {
  const formatted = email.trim().toLowerCase();

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const isValid = emailRegex.test(formatted);

  return isValid ? formatted : null;
};

export const processIndianPhoneNumber = (phoneNumber) => {
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  let isValid = false;
  let formattedNumber = null;

  if (digitsOnly.length === 10 && /^[6-9]\d{9}$/.test(digitsOnly)) {
    isValid = true;
    formattedNumber = digitsOnly;
  } else if (digitsOnly.length === 11 && /^0[6-9]\d{9}$/.test(digitsOnly)) {
    isValid = true;
    formattedNumber = digitsOnly.substring(1);
  } else if (digitsOnly.length === 12 && /^91[6-9]\d{9}$/.test(digitsOnly)) {
    isValid = true;
    formattedNumber = digitsOnly.substring(2);
  }

  return isValid ? formattedNumber : null;
};

export const phoneNumberSchemaValidation = (value) => {
  if (!value) return false;
  if (!/^[0-9]{10}$/.test(value)) return false;
  const digitsOnly = value.replace(/\D/g, '');
  return (
    /^[6-9]\d{9}$/.test(digitsOnly) ||
    /^0[6-9]\d{9}$/.test(digitsOnly) ||
    /^91[6-9]\d{9}$/.test(digitsOnly)
  );
};

export const emailSchemaValidation = (value) => {
  if (!value) return false;

  // - local part must start with [A-Za-z0-9]
  // - no consecutive dots, no leading/trailing dot in local part
  // - allowed special chars after first char: .!#$%&'*+/=?^_`{|}~-
  // - domain labels start/end with alnum, may contain hyphens; TLD >= 2 letters
  const emailRegex =
    /^(?!.*\.\.)(?!\.)[A-Za-z0-9](?:[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]{0,62}[A-Za-z0-9!#$%&'*+/=?^_`{|}~-])?@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z]{2,})+$/;

  return emailRegex.test(value);
};

export const getEpcPartnerUrl = (type = 'epcPartner') => {
  const baseUrl =
    process.env.REACT_APP_PARTNER_URL ||
    (process.env.NODE_ENV === 'production'
      ? 'https://partner.solfin.co.in'
      : 'https://stg-partner.solfin.co.in');

  if (type === 'epcPartnerLogin') {
    return `${baseUrl}/login`;
  }

  return baseUrl;
};

/**
 * Redirects to EPC partner URL based on current environment
 * @param {string} type - URL type ('epcPartner' or 'epcPartnerLogin')
 * @param {boolean} newTab - Whether to open in new tab (default: true)
 */
export const redirectToEpcPartner = (type = 'epcPartner', newTab = true) => {
  const url = getEpcPartnerUrl(type);

  if (newTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
};

export const formatCurrencyInCr = (amount) => {
  if (amount < 0) {
    return `₹0 Cr`;
  }
  const crores = amount / 10000000;
  return `₹${crores.toFixed(2)} Cr`;
};

export const formatNumber = (num) => {
  return new Intl.NumberFormat('en-IN').format(num);
};
